import { useMemo } from 'react';
import type { ProductPerk } from '../../domain/entities/catalog.entity';
import {
  useProductPerks,
  useApplyProductPerkBatch,
} from '../hooks/use-product-perks';
import { useAllPerkGroups } from '../hooks/use-perks';
import {
  DELIVERS_PER_CYCLE_LABELS,
  OVERDUE_POLICY_LABELS,
  describeGrantPolicyValues,
  grantPolicyLabel,
} from '../labels';
import { indexPerkForms } from '../perk-index';
import { PerkAssociationEditor } from './PerkAssociationEditor';
import type {
  ExistingPerkRow,
  PerkAssociationSeedRow,
  PerkAssociationTarget,
} from './PerkAssociationEditor';
import { ScopeBadgeList } from '@/shared/scopes/ScopeBadge';

/** A associação inteira viaja junto do id — o card da esquerda é desenhado aqui. */
type ProductPerkRow = ExistingPerkRow & { perk: ProductPerk };

interface ProductPerksEditorModalProps {
  productId: string;
  productName: string;
  open: boolean;
  onClose: () => void;
  /** Criar produto a partir de um existente (§8 do plano): perks da origem. */
  initialAdditions?: PerkAssociationSeedRow[];
}

/**
 * O alvo **produto** do editor de associação. É o único com política de
 * inadimplência (`overduePolicy`) e com entrega por ciclo
 * (`deliversPerCycle`), e o `perkId` não se repete: uma associação
 * por par (produto, perk) — trocar valores é remover e re-adicionar, o que o
 * editor faz no mesmo lote.
 */
export function ProductPerksEditorModal({
  productId,
  productName,
  open,
  onClose,
  initialAdditions,
}: ProductPerksEditorModalProps) {
  const { data: perks = [], isLoading } = useProductPerks(productId);
  const { data: groups } = useAllPerkGroups();
  const { mutateAsync } = useApplyProductPerkBatch(productId);

  const perkIndex = useMemo(() => indexPerkForms(groups), [groups]);

  const target: PerkAssociationTarget<ProductPerkRow> = useMemo(
    () => ({
      title: `Perks de ${productName}`,
      description:
        'Monte o conjunto de perks que a compra deste produto concede. Nada é gravado até aplicar.',
      duplicatesAllowed: false,
      extraFields: ['overduePolicy', 'deliversPerCycle'],
      supportsTargetScopes: true,
      removal: { label: 'Remover', needsReason: false },
      existing: perks.map((perk) => ({
        id: perk.id,
        perkId: perk.perkId,
        perk,
      })),
      existingLoading: isLoading,
      editableValues: (row) => ({
        durationInMilliseconds: row.perk.durationInMilliseconds,
        maxUsages: row.perk.maxUsages,
        renewEveryInMilliseconds: row.perk.renewEveryInMilliseconds,
        targetScopes: row.perk.targetScopes,
        overduePolicy: row.perk.overduePolicy,
        deliversPerCycle: row.perk.deliversPerCycle,
      }),
      renderExisting: ({ perk }) => {
        const resolved = perkIndex.get(perk.perkId);
        return (
          <div className="space-y-0.5">
            <span className="text-(--color-text-primary) block text-sm">
              {resolved?.group.name ?? resolved?.group.key ?? perk.perkId}
            </span>
            <span className="text-(--color-text-secondary) block text-xs">
              {resolved ? grantPolicyLabel(resolved.form.grantPolicyKind) : '—'}
              {' · '}
              {describeGrantPolicyValues(perk)}
              {' · '}
              {OVERDUE_POLICY_LABELS[perk.overduePolicy]}
              {perk.deliversPerCycle && ` · ${DELIVERS_PER_CYCLE_LABELS.true}`}
            </span>
            {perk.targetScopes.length > 0 && (
              <ScopeBadgeList ids={perk.targetScopes} />
            )}
          </div>
        );
      },
      submit: async ({ add, remove }) => {
        await mutateAsync({
          add: add.map((addition) => ({
            perkId: addition.perkId,
            ...addition.grantPolicyParams,
            ...(addition.targetScopes.length > 0
              ? { targetScopes: addition.targetScopes }
              : {}),
            overduePolicy: addition.overduePolicy,
            deliversPerCycle: addition.deliversPerCycle,
          })),
          remove: remove.map((row) => row.id),
        });
      },
    }),
    [productName, perks, isLoading, perkIndex, mutateAsync],
  );

  return (
    <PerkAssociationEditor
      open={open}
      onClose={onClose}
      target={target}
      initialAdditions={initialAdditions}
    />
  );
}
